import type {
  BlockwalkerActivity,
  CampaignBlockName,
} from '@goldfishcode/kingmakerdata-api-sdk/libs/api/campaign/models';
import { BlockTypes } from '@goldfishcode/kingmakerdata-api-sdk/libs/api/campaign/models';
import type {
  DashboardChartData,
  DashboardFilterParams,
} from '@goldfishcode/kingmakerdata-api-sdk/libs/api/dashboard/models';
import { ChartChoice, Operator } from '@goldfishcode/kingmakerdata-api-sdk/libs/api/dashboard/models';
import type { User } from '@goldfishcode/kingmakerdata-api-sdk/libs/api/user/models';
import type { Pagination } from '@goldfishcode/kingmakerdata-api-sdk/libs/type';
import { message } from 'antd';
import { cloneDeep, includes } from 'lodash';
import { apiIns } from 'src/config/apiClient';
import { getCandidateId } from 'src/utils/common';
import { ableLoadingDefault } from 'src/utils/dashboard';

import { appAction } from '../app/action';
import type { RootState } from '../model';
import { createAppAsyncThunk } from '../model';
import type { Dashboard, FieldOperationsQuery } from './model';
import { dashboardSliceActions } from './reducer';

let lastRequestId = 0;

export const getListQuestions = createAppAsyncThunk(
  '@dashboard/getListQuestions',
  async (surveyIds: string[], { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const existingSurveyIds = Object.keys(state.dashboard.questionsBySurvey || {});

      const removedSurveyIds = existingSurveyIds.filter(id => !surveyIds.includes(id));

      removedSurveyIds.forEach(id => {
        dispatch(dashboardSliceActions.removeSurveyQuestions(id));
      });

      const newSurveyIds = surveyIds.filter(id => !existingSurveyIds.includes(id));

      const allResults = await Promise.all(
        newSurveyIds.map(async id => {
          try {
            const response = await apiIns.survey.getQuestions(id);

            return {
              id,
              questions: response || [],
            };
          } catch (e) {
            message.error(`Failed to load questions for survey ${id} error: ${e.message}`);
            return { id, questions: [] };
          }
        }),
      );

      return allResults;
    } catch (error) {
      message.error(error.message);
      return [];
    }
  },
);

export const getSurveyNames = createAppAsyncThunk('@dashboard/getSurveyNames', async (profile: User) => {
  try {
    const candidateId = getCandidateId(profile);

    if (!candidateId) {
      return [];
    }

    const results = await apiIns.survey.getSurveyNames(candidateId);

    const surveysList = results.map(survey => ({
      ...survey,
      id: survey.id.toString(), // Ensure id is a string
    }));

    return surveysList;
  } catch (error) {
    message.error(error.message);
  }
});

// ================================= Converting ================
export const getDashboardSettings = createAppAsyncThunk(
  '@dashboard/getDashboardSettings',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(appAction.showLoading());

      return await apiIns.dashboard.settings();
    } catch (error) {
      message.error(error.message);
      return rejectWithValue(error?.message);
    } finally {
      await dispatch(appAction.hideLoading());
      await dispatch(dashboardSliceActions.hideDashboardLoading());
    }
  },
);

export const getDashboardData = createAppAsyncThunk(
  '@dashboard/getDashboardData',
  async (payload: DashboardFilterParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const requestId = ++lastRequestId;

      lastRequestId = requestId;

      const { isLoadingDefault, changedKeys, tagAndOr, surveyAndOr } = getState().dashboard;
      const isCountyChanged = includes(changedKeys, 'select_control_county_dropdown'); // if there is a change in county dropdown from dashboard filter params

      const ableLoading = ableLoadingDefault(payload.select_control_party, payload.select_control_vote_prediction);
      const enableQuery = !isLoadingDefault || isLoadingDefault !== ableLoading || isCountyChanged;

      if (enableQuery) {
        const filterParams = {
          ...payload,
          loading_default: ableLoading,
        };

        await dispatch(dashboardSliceActions.setIsLoadingChart(true));

        const [dashboardData, { tag_dropdown, tags }, { survey_chart }] = await Promise.all([
          apiIns.dashboard.queryChart({
            ...filterParams,
            tag_operator: tagAndOr === 'AND' ? Operator.AND : Operator.OR,
            survey_operator: surveyAndOr === 'AND' ? Operator.AND : Operator.OR,
          }),
          apiIns.dashboard.queryChart({
            ...filterParams,
            chart_choice: ChartChoice.TAG,
            tag_operator: tagAndOr === 'AND' ? Operator.AND : Operator.OR,
            survey_operator: surveyAndOr === 'AND' ? Operator.AND : Operator.OR,
          }),
          apiIns.dashboard.queryChart({
            ...filterParams,
            chart_choice: ChartChoice.SURVEY,
            tag_operator: tagAndOr === 'AND' ? Operator.AND : Operator.OR,
            survey_operator: surveyAndOr === 'AND' ? Operator.AND : Operator.OR,
          }),
        ]);

        await dispatch(dashboardSliceActions.setIsLoadingDefault(ableLoading));

        if (requestId !== lastRequestId) {
          return rejectWithValue('Request outdated');
        }

        return {
          ...dashboardData,
          tag_dropdown,
          tags,
          survey_chart: survey_chart ?? {},
        } as DashboardChartData;
      }
      return rejectWithValue('Loading is not enabled');
    } catch (error: any) {
      message.error(error.message);
      return rejectWithValue(error?.message);
    } finally {
      await dispatch(dashboardSliceActions.setIsLoadingChart(false));
      await dispatch(dashboardSliceActions.hideDashboardLoading());
    }
  },
);

export const getTagConfig = createAppAsyncThunk('@dashboard/getTagConfig', async (_, { rejectWithValue }) => {
  try {
    const [colors, icons] = await Promise.all([apiIns.dashboard.tagColors(), apiIns.dashboard.tagIcons()]);
    return { colors, icons };
  } catch (error) {
    message.error(error.message);
    return rejectWithValue(error?.message);
  }
});

export const getTags = createAppAsyncThunk('@dashboard/getTags', async (candidateId: string, { rejectWithValue }) => {
  try {
    return await apiIns.dashboard.tags(candidateId);
  } catch (error: any) {
    message.error(error.message);
    return rejectWithValue(error?.message);
  }
});

export const getFieldOperations = createAppAsyncThunk(
  '@dashboard/getFieldOperations',
  async (payload: Dashboard['fieldOperationsQuery'], { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const fieldOperationsQuery = state.dashboard.fieldOperationsQuery;
      const newFilter: Partial<FieldOperationsQuery> = {
        ...fieldOperationsQuery,
        ...payload,
      };
      await dispatch(
        dashboardSliceActions.setFieldOperations({
          ...newFilter,
          isLoading: true,
        }),
      );

      const candidateId = getCandidateId(state.auth.myProfile);

      if (newFilter.isBlockwalking) {
        const blocks: CampaignBlockName[] = await apiIns.campaign.allCampaignBlockNames(
          candidateId,
          newFilter.searchValue,
          undefined,
          BlockTypes.BLOCK_WALK,
        );
        await dispatch(dashboardSliceActions.setBlocks(blocks));
      } else {
        const blocks: CampaignBlockName[] = await apiIns.campaign.allCampaignBlockNames(
          candidateId,
          newFilter.searchValue,
          undefined,
          BlockTypes.PHONE_BANK,
        );

        await dispatch(dashboardSliceActions.setBlocks(blocks));
      }
    } catch (error: any) {
      message.error(error.message);
      return rejectWithValue(error?.message);
    } finally {
      await dispatch(dashboardSliceActions.setFieldOperations({ isLoading: false }));
    }
  },
);

export const getListActivity = createAppAsyncThunk(
  '@dashboard/getListActivity',
  async (payload: Dashboard['activityQuery'], { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const fieldOperationsSelected = state.dashboard.fieldOperationsSelected;
      if (!fieldOperationsSelected) {
        dashboardSliceActions.setListActivity([]);
        return;
      }

      const currentActivityQuery = state.dashboard.activityQuery;

      const newActivityQuery: any = {
        ...currentActivityQuery,
        ...payload,
        isLoading: true,
        block_ids: fieldOperationsSelected?.value ? fieldOperationsSelected?.value : undefined,
      };
      await dispatch(dashboardSliceActions.setActivityQuery({ ...newActivityQuery, hasMore: false }));

      const params = cloneDeep(newActivityQuery);
      delete params.isLoading;
      delete params.hasMore;

      const listActivity: Pagination<BlockwalkerActivity> = await apiIns.campaign.activities(params);
      await dispatch(dashboardSliceActions.setActivityQuery({ ...newActivityQuery, hasMore: !!listActivity.next }));
      await dispatch(dashboardSliceActions.setListActivity(listActivity.results));
    } catch (error: any) {
      message.error(error.message);
      return rejectWithValue(error?.message);
    } finally {
      await dispatch(dashboardSliceActions.setActivityQuery({ isLoading: false }));
    }
  },
);
